import React, { memo, useState } from 'react'
import styled from 'styled-components'
import { useMediaPredicate } from 'react-media-hook'
import { Link } from 'react-router-dom'
import { Button } from 'primereact/button'

import { LoadBalancerSetupGuideDialog } from '../dialogs/load-balancer-setup-guide-dialog'
import { WidgetLayout } from '../../../../lib/widget-layout'
import Text from '../../../../ui/Text'
import { LoadBalancerGroup, MeasurementDeviceExtendedGetModel } from '../../../redux/api/web-api'
import {
    SIconValue,
    SProperties,
    SPropertiesValue,
} from '../../../../support/features/charge-point-properties-widget/charge-point-properties-widget.style'
import { SLabelText, SSubHeadings } from '../../../styles/Form'
import { GoToIcon } from '../../../../lib/icons/goto-icon'
import { theme } from '../../../../lib/theme'
import { SLabel } from '../../../../ui/Tabs'
import ZapIcon from '../../../../lib/icons/bolt-icon/bolt-icon'
import { Toggle } from '../../../components/Toggle'
import RowDivider from '../../../components/RowDivider'

export type LoadbalancerDetailsWidgetProps = {
    group: LoadBalancerGroup
    loading: boolean
    measurementDevice?: MeasurementDeviceExtendedGetModel
}

export const SIcon = styled.span`
    display: flex;
    gap: 10px;
`

export const LoadbalancerDetailsWidget = memo(function LoadbalancerDetailsWidget({
    group,
    measurementDevice,
    loading,
}: LoadbalancerDetailsWidgetProps): React.ReactElement {
    const [isSetupGuideDialogVisible, setIsSetupGuideDialogVisible] = useState(false)
    const isMobileScreen = useMediaPredicate('(max-width: 900px)')

    return (
        <>
            <div className="grid">
                <div className={isMobileScreen ? 'col-12' : 'col-6'}>
                    <WidgetLayout data-testid="details" title={<Text code="lblLBInformation" />} isLoading={loading}>
                        <SProperties>
                            <SPropertiesValue>
                                <SLabelText>
                                    <Text code="lblId" />
                                </SLabelText>
                                <div data-testid="lb-group-id">{group?.id}</div>
                            </SPropertiesValue>

                            {group?.description && (
                                <SPropertiesValue>
                                    <SLabelText>
                                        <Text code="lblDescription" />
                                    </SLabelText>
                                    <div data-testid="lb-group-description">{group?.description}</div>
                                </SPropertiesValue>
                            )}
                            <SPropertiesValue>
                                <SLabelText>
                                    <Text code="lblOrganisation" />
                                </SLabelText>

                                <SLabel> {group?.companyName}</SLabel>
                            </SPropertiesValue>
                        </SProperties>
                        {measurementDevice && measurementDevice.id && (
                            <>
                                <RowDivider />
                                <SSubHeadings>
                                    <ZapIcon size={20} />
                                    <Text code="lblMeasurementDevice" />
                                </SSubHeadings>
                                <SProperties>
                                    <SPropertiesValue>
                                        <SLabelText>
                                            <Text code="lblSerialNumber" />
                                        </SLabelText>
                                        <SIconValue>
                                            <>
                                                <SLabel>
                                                    {measurementDevice?.serialNumber}{' '}
                                                    <span>
                                                        {measurementDevice.active ? (
                                                            <Text code="lblActiveStatusPar" />
                                                        ) : (
                                                            <Text code="lblInactiveSchedule" />
                                                        )}
                                                    </span>
                                                </SLabel>
                                                <Link
                                                    to={'/measurementDevice/' + measurementDevice?.id}
                                                    target="_blank"
                                                >
                                                    <GoToIcon color={theme.brandColors.CA_Blue} size={16} />
                                                </Link>
                                            </>
                                        </SIconValue>
                                    </SPropertiesValue>

                                    <SPropertiesValue>
                                        <SLabelText>
                                            <Text code="lblName" />
                                        </SLabelText>
                                        <div data-testid={'devicename-' + measurementDevice?.name}>
                                            {measurementDevice?.name}
                                        </div>
                                    </SPropertiesValue>
                                </SProperties>
                            </>
                        )}
                    </WidgetLayout>
                </div>
                <div className={isMobileScreen ? 'col-12' : 'col-6'}>
                    <WidgetLayout
                        data-testid="details"
                        title={<Text code="lblElectricalSettings" />}
                        isLoading={loading}
                        headerButtons={
                            <Button
                                className="ml-2 p-1"
                                text
                                severity="info"
                                onClick={() => setIsSetupGuideDialogVisible(true)}
                            >
                                <Text code="btnViewInfographics" />
                            </Button>
                        }
                    >
                        <SProperties>
                            <SPropertiesValue>
                                <SLabelText>
                                    <Text code="lblPhases" />
                                </SLabelText>
                                <div data-testid="lb-group-phases">{group?.allowedPhases}</div>
                            </SPropertiesValue>
                            {group?.supplyMaxCurrent && (
                                <SPropertiesValue>
                                    <SLabelText>
                                        <Text code="lblSupplyMaxCurrent" />
                                    </SLabelText>
                                    <div data-testid={'supplyCurrent-' + group?.id}> {group?.supplyMaxCurrent}</div>
                                </SPropertiesValue>
                            )}
                            <SPropertiesValue>
                                <SLabelText>
                                    <Text code="lblMinCurrentPerCharger" />
                                </SLabelText>
                                <div data-testid={'minCurrent-' + group?.id}> {group?.minCurrentPerChargePoint}</div>
                            </SPropertiesValue>
                            <SPropertiesValue>
                                {measurementDevice ? (
                                    <SLabelText>
                                        <Text code="lblMaxCurrentPerPhaseAGOffline" />
                                    </SLabelText>
                                ) : (
                                    <SLabelText>
                                        <Text code="lblMaxCurrentPerPhase" />
                                    </SLabelText>
                                )}
                                <div data-testid="lb-group-phases"> {group?.maxCurrentPerPhase}</div>
                            </SPropertiesValue>

                            {measurementDevice && (
                                <SPropertiesValue>
                                    <SLabelText>
                                        <Text code="lblMaxCurrentPerPhaseAGOnline" />
                                    </SLabelText>
                                    <div data-testid={'maxCurrentPhase-' + group?.id}>
                                        {group?.deviceControlledMaxCurrentPerPhase}
                                    </div>
                                </SPropertiesValue>
                            )}
                        </SProperties>
                        <RowDivider />
                        {group?.schukoEnabled && (
                            <SProperties>
                                <SPropertiesValue>
                                    <span className="flex">
                                        <SLabelText>
                                            <Text code="lblSocketEnabled" />
                                        </SLabelText>
                                        <Toggle
                                            size="md"
                                            data-testid="socket-enabled-toggle"
                                            isToggled={group?.schukoEnabled}
                                            disabled
                                        ></Toggle>
                                    </span>
                                </SPropertiesValue>
                            </SProperties>
                        )}

                        {group?.chargePointsIncludedInMeasurement && (
                            <SProperties>
                                <SPropertiesValue>
                                    <span className="flex">
                                        <SLabelText>
                                            <Text code="lblChargersIncludedInPowerConsumptionText" />
                                        </SLabelText>
                                        <Toggle
                                            size="md"
                                            data-testid="chargepoints-included-toggle"
                                            isToggled={group?.chargePointsIncludedInMeasurement}
                                            disabled
                                        ></Toggle>
                                    </span>
                                </SPropertiesValue>
                            </SProperties>
                        )}
                    </WidgetLayout>
                </div>
            </div>
            <LoadBalancerSetupGuideDialog
                visible={isSetupGuideDialogVisible}
                onHide={() => setIsSetupGuideDialogVisible(false)}
                measurementDeviceId={measurementDevice?.id || 0}
            />
        </>
    )
})
